import React, { useState, useMemo } from "react";
import {
  Table,
  Stack,
  Text,
  Select,
  ScrollArea,
  Badge,
  Skeleton,
  Alert,
  Pagination,
  Flex,
  ActionIcon,
  Modal,
  Button,
  Group,
} from "@mantine/core";
import { IconAlertCircle, IconTrash, IconPencil } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "react-query";
import { removeUserAdmin, updateUserHourlyRateAdmin } from "api";
import { showNotification } from "@mantine/notifications";
import { IconCheck, IconX } from "@tabler/icons-react";
import { useUser } from "stores/useUser";
import EditRateModal from "./EditRateModal";

const UsersTable = ({
  data,
  isLoading,
  isError,
  departmentId,
  departmentName,
  searchQuery,
  onSearchChange,
  orgId,
}) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState(null);
  const [sortOrder, setSortOrder] = useState("asc");
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [userToEdit, setUserToEdit] = useState(null);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const formatCurrency = (income) => {
    if (!income || !income.amount) return "N/A";

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: income.currency || "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.ceil(income.amount));
  };

  const formatManagerName = (managerEmail) => {
    if (!managerEmail) return "No Manager";
    return managerEmail;
  };

  // Mutation for removing user
  const removeUserMutation = useMutation(
    ({ orgId, finchId }) => removeUserAdmin(orgId, finchId),
    {
      onSuccess: () => {
        showNotification({
          title: "Success",
          message: "User removed successfully!",
          color: "green",
          icon: <IconCheck size={16} />,
        });
        // Invalidate and refetch department users
        queryClient.invalidateQueries(["departmentUsers", orgId, departmentName]);
        setDeleteModalOpened(false);
        setUserToDelete(null);
      },
      onError: (error) => {
        console.error("Error removing user:", error);
        showNotification({
          title: "Error",
          message: error.message || "Failed to remove user. Please try again.",
          color: "red",
          icon: <IconX size={16} />,
        });
        setDeleteModalOpened(false);
        setUserToDelete(null);
      },
    }
  );

  // Mutation for updating a user's hourly rate
  const updateRateMutation = useMutation(
    ({ finchId, amount }) =>
      updateUserHourlyRateAdmin(
        user.subscription.organization_id,
        finchId,
        amount
      ),
    {
      onSuccess: () => {
        showNotification({
          title: "Success",
          message: "Hourly rate updated successfully!",
          color: "green",
          icon: <IconCheck size={16} />,
        });
        queryClient.invalidateQueries([
          "departmentUsers",
          orgId,
          departmentName,
        ]);
        setEditModalOpened(false);
        setUserToEdit(null);
      },
      onError: (error) => {
        console.error("Error updating hourly rate:", error);
        showNotification({
          title: "Error",
          message:
            error.message || "Failed to update hourly rate. Please try again.",
          color: "red",
          icon: <IconX size={16} />,
        });
      },
    }
  );

  const handleEditClick = (user) => {
    setUserToEdit(user);
    setEditModalOpened(true);
  };

  const handleConfirmEdit = (amount) => {
    if (userToEdit) {
      updateRateMutation.mutate({ finchId: userToEdit.finchId, amount });
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteModalOpened(true);
  };

  const handleConfirmDelete = () => {
    if (userToDelete) {
      removeUserMutation.mutate({
        orgId: user.subscription.organization_id,
        finchId: userToDelete.finchId,
      });
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    if (!data) return [];

    let filtered = data.filter((user) => {
      const searchLower = searchQuery.toLowerCase();
      return (
        user.email.toLowerCase().includes(searchLower) ||
        user.first_name.toLowerCase().includes(searchLower) ||
        user.last_name.toLowerCase().includes(searchLower) ||
        user.title.toLowerCase().includes(searchLower) ||
        formatManagerName(user.managerEmail)
          .toLowerCase()
          .includes(searchLower)
      );
    });

    if (sortBy) {
      filtered.sort((a, b) => {
        let aVal, bVal;

        switch (sortBy) {
          case "firstName":
            aVal = a.first_name;
            bVal = b.first_name;
            break;
          case "lastName":
            aVal = a.last_name;
            bVal = b.last_name;
            break;
          case "income":
            aVal = a.income?.amount || 0;
            bVal = b.income?.amount || 0;
            break;
          case "managerName":
            aVal = formatManagerName(a.managerEmail);
            bVal = formatManagerName(b.managerEmail);
            break;
          default:
            aVal = a[sortBy];
            bVal = b[sortBy];
        }

        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (sortOrder === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [data, searchQuery, sortBy, sortOrder]);

  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAndSortedUsers.slice(startIndex, endIndex);
  }, [filteredAndSortedUsers, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedUsers.length / pageSize);

  const renderTable = () => {
    if (isLoading) {
      return (
        <ScrollArea.Autosize maxHeight={400}>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th style={{ minWidth: "200px" }}>Email</th>
                <th style={{ minWidth: "120px" }}>First Name</th>
                <th style={{ minWidth: "120px" }}>Last Name</th>
                <th style={{ minWidth: "180px" }}>Title</th>
                <th style={{ minWidth: "100px" }}>Income/hr</th>
                <th style={{ minWidth: "150px" }}>Manager</th>
                <th style={{ minWidth: "150px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, index) => (
                <tr key={`skeleton-${index}`}>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                  <td>
                    <Skeleton height={20} />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ScrollArea.Autosize>
      );
    }

    if (isError) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Users"
          color="red"
          variant="light"
        >
          Failed to load user data. Please try again later.
        </Alert>
      );
    }

    if (paginatedUsers.length === 0) {
      return (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="No Users Found"
          color="yellow"
          variant="light"
        >
          {searchQuery
            ? "No users match your search criteria."
            : "No users available in this department."}
        </Alert>
      );
    }

    return (
      <ScrollArea.Autosize maxHeight={400}>
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th
                style={{ cursor: "pointer", minWidth: "200px" }}
                onClick={() => handleSort("email")}
              >
                Email {sortBy === "email" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "120px" }}
                onClick={() => handleSort("firstName")}
              >
                First Name{" "}
                {sortBy === "firstName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "120px" }}
                onClick={() => handleSort("lastName")}
              >
                Last Name{" "}
                {sortBy === "lastName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "180px" }}
                onClick={() => handleSort("title")}
              >
                Title {sortBy === "title" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "100px" }}
                onClick={() => handleSort("income")}
              >
                Income/hr{" "}
                {sortBy === "income" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "150px" }}
                onClick={() => handleSort("managerName")}
              >
                Manager{" "}
                {sortBy === "managerName" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th
                style={{ cursor: "pointer", minWidth: "150px" }}
                onClick={() => handleSort("actions")}
              >
                Actions{" "}
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.map((user) => (
              <tr key={user.finchId}>
                <td>
                  <Text size="sm" title={user.email}>
                    {user.email}
                  </Text>
                </td>
                <td>
                  <Text size="sm">{user.first_name}</Text>
                </td>
                <td>
                  <Text size="sm">{user.last_name}</Text>
                </td>
                <td>
                  <Text size="sm" title={user.title}>
                    {user.title}
                  </Text>
                </td>
                <td>
                  <Badge size="sm" variant="light" color="green">
                    {formatCurrency(user.income)}
                  </Badge>
                </td>
                <td>
                  <Text size="sm" c="dimmed">
                    {formatManagerName(user.managerEmail)}
                  </Text>
                </td>
                <td>
                  <Group gap="xs">
                    <ActionIcon
                      color="blue"
                      variant="light"
                      aria-label="Edit hourly rate"
                      onClick={() => handleEditClick(user)}
                    >
                      <IconPencil size={20} />
                    </ActionIcon>
                    <ActionIcon
                      color="red"
                      variant="light"
                      aria-label="Delete"
                      onClick={() => handleDeleteClick(user)}
                      loading={removeUserMutation.isLoading}
                    >
                      <IconTrash size={20} />
                    </ActionIcon>
                  </Group>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </ScrollArea.Autosize>
    );
  };

  return (
    <Stack gap="md" style={{ height: "100%", minHeight: "500px" }}>
      {/* Fixed height table container */}
      <div style={{ flex: 1, minHeight: "400px" }}>{renderTable()}</div>

      {/* Fixed footer with pagination controls */}
      <div style={{ marginTop: "auto" }}>
        <Flex align="center" justify="space-between" mb="md">
          <Select
            label="Page size"
            value={pageSize.toString()}
            onChange={(value) => {
              setPageSize(parseInt(value));
              setCurrentPage(1);
            }}
            data={[
              { value: "5", label: "5 per page" },
              { value: "10", label: "10 per page" },
              { value: "25", label: "25 per page" },
            ]}
            style={{ minWidth: 120 }}
          />

          {totalPages > 1 && (
            <Pagination
              value={currentPage}
              onChange={setCurrentPage}
              total={totalPages}
              size="sm"
            />
          )}
        </Flex>

        <Text size="sm" c="dimmed" ta="center">
          Showing {paginatedUsers.length} of {filteredAndSortedUsers.length}{" "}
          users
        </Text>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpened}
        onClose={() => {
          setDeleteModalOpened(false);
          setUserToDelete(null);
        }}
        title="Confirm User Removal"
        centered
      >
        <Stack gap="md">
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Warning"
            color="red"
            variant="light"
          >
            Are you sure you want to remove this user? This action cannot be undone.
          </Alert>
          
          {userToDelete && (
            <div>
              <Text size="sm" fw={500}>User Details:</Text>
              <Text size="sm" c="dimmed">
                Name: {userToDelete.first_name} {userToDelete.last_name}
              </Text>
              <Text size="sm" c="dimmed">
                Email: {userToDelete.email}
              </Text>
              <Text size="sm" c="dimmed">
                Title: {userToDelete.title}
              </Text>
            </div>
          )}

          <Group justify="flex-end" mt="md">
            <Button
              variant="subtle"
              onClick={() => {
                setDeleteModalOpened(false);
                setUserToDelete(null);
              }}
              disabled={removeUserMutation.isLoading}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleConfirmDelete}
              loading={removeUserMutation.isLoading}
            >
              Remove User
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit Hourly Rate Modal */}
      <EditRateModal
        opened={editModalOpened}
        onClose={() => {
          setEditModalOpened(false);
          setUserToEdit(null);
        }}
        user={userToEdit}
        onSubmit={handleConfirmEdit}
        isSubmitting={updateRateMutation.isLoading}
      />
    </Stack>
  );
};

export default UsersTable;
